const DEFAULT_DEBUG_KEY = "lv:page-loading-debug";

function removeLastOccurrence(items, value) {
  const index = items.lastIndexOf(value);
  if (index >= 0) items.splice(index, 1);
}

function stableStringify(value) {
  if (value === null) return "null";
  if (Array.isArray(value)) return `[${value.map((item) => stableStringify(item)).join(",")}]`;
  if (typeof value === "object") {
    const keys = Object.keys(value).sort();
    return `{${keys.map((key) => `${key}:${stableStringify(value[key])}`).join(",")}}`;
  }
  return String(value);
}

export function createPageLoadingTracker(options = {}) {
  const ignoredKinds = options.ignoredKinds || new Set(["error"]);
  const debugStorageKey = options.debugStorageKey || DEFAULT_DEBUG_KEY;
  const onActiveCountChange = options.onActiveCountChange || (() => {});

  const tokenCounts = new Map();
  const tokenStack = [];
  const elementIds = new WeakMap();
  let nextElementId = 1;
  let activeCount = 0;

  const isDebugEnabled = () => {
    try {
      return window.localStorage.getItem(debugStorageKey) === "1";
    } catch (_error) {
      return false;
    }
  };

  const debug = (action, payload = {}) => {
    if (!isDebugEnabled()) return;
    console.debug("[PageLoadingTracker]", action, payload);
  };

  const targetToken = (target) => {
    if (!target) return "none";
    if (typeof target === "string") return `selector:${target}`;
    if (target instanceof Element) {
      if (target.id) return `id:${target.id}`;
      if (!elementIds.has(target)) {
        elementIds.set(target, `anon:${nextElementId}`);
        nextElementId += 1;
      }
      const nodeId = elementIds.get(target);
      return `node:${target.tagName.toLowerCase()}:${nodeId}`;
    }
    if (typeof target === "object") {
      return `object:${stableStringify(target)}`;
    }
    return `other:${String(target)}`;
  };

  const detailToken = (detail = {}) => {
    const kind = typeof detail.kind === "string" ? detail.kind : "unknown";
    const tokenParts = [
      `kind:${kind}`,
      `to:${typeof detail.to === "string" ? detail.to : ""}`,
      `href:${typeof detail.href === "string" ? detail.href : ""}`,
      `event:${typeof detail.event === "string" ? detail.event : ""}`,
      `type:${typeof detail.type === "string" ? detail.type : ""}`,
      `ref:${detail.ref ?? ""}`,
      `target:${targetToken(detail.target)}`,
    ];
    return tokenParts.join("|");
  };

  const decrementToken = (token) => {
    const current = tokenCounts.get(token);
    if (typeof current !== "number" || current <= 0) return false;

    if (current === 1) tokenCounts.delete(token);
    else tokenCounts.set(token, current - 1);

    removeLastOccurrence(tokenStack, token);
    activeCount = Math.max(0, activeCount - 1);
    return true;
  };

  const start = (detail = {}) => {
    const kind = typeof detail.kind === "string" ? detail.kind : "unknown";
    if (ignoredKinds.has(kind)) {
      debug("start-ignored-kind", { kind, detail });
      return;
    }

    const token = detailToken(detail);
    tokenCounts.set(token, (tokenCounts.get(token) || 0) + 1);
    tokenStack.push(token);
    activeCount += 1;
    debug("start", { kind, token, detail, activeCount });
    onActiveCountChange(activeCount, { phase: "start", token, detail });
  };

  const stop = (detail = {}) => {
    const kind = typeof detail.kind === "string" ? detail.kind : "unknown";
    const token = detailToken(detail);

    if (ignoredKinds.has(kind) && !tokenCounts.has(token)) {
      debug("stop-ignored-kind", { kind, token, detail });
      return;
    }

    let removed = decrementToken(token);

    if (!removed && tokenStack.length > 0) {
      const fallbackToken = tokenStack[tokenStack.length - 1];
      removed = decrementToken(fallbackToken);
      debug("stop-fallback", { requestedToken: token, fallbackToken, detail });
    }

    if (!removed) {
      debug("stop-without-active-start", { token, detail });
      return;
    }

    debug("stop", { token, detail, activeCount });
    onActiveCountChange(activeCount, { phase: "stop", token, detail });
  };

  const onStart = (event) => start(event?.detail || {});
  const onStop = (event) => stop(event?.detail || {});

  const reset = () => {
    tokenCounts.clear();
    tokenStack.length = 0;
    activeCount = 0;
    debug("reset");
    onActiveCountChange(activeCount, { phase: "reset", token: null, detail: {} });
  };

  return {
    getActiveCount: () => activeCount,
    onStart,
    onStop,
    reset,
  };
}
